import dayjs from "dayjs";
import { useEffect } from "react";
import { sortDirectionMap } from "constants/sortDirectionMap";
import {
  useCustomGetJobPhasesForDropdownLazyQuery,
  useCustomGetJobPhasesLazyQuery,
  useCustomGetJobPhasesScheduleBlocksLazyQuery,
} from "generated/graphql";
import { jobPhasesInitialSortKeyValue, jobPhasesSortKeyMap } from "../useJobPhasesTableData";

const getFormattedFilterDate = (startsAt?: string | null) =>
  startsAt
    ? dayjs.utc(new Date(`${startsAt} 00:00:00`)).format("YYYY-MM-DD HH:mm:ss")
    : null;

const getSharedVariables = ({ filtering, sorting, offset }) => ({
  search: filtering.debouncedSearchTerm,
  sortDirection: sortDirectionMap[sorting.sortDirection],
  sortKey: jobPhasesSortKeyMap[sorting.sortKey.toLowerCase()] || jobPhasesInitialSortKeyValue,
  first: offset.first,
  page: offset.page,
  companyId: filtering.companyId ?? null,
  userId: filtering.userID ?? null,
  crewId: filtering.crewID ?? null,
  foremanUserId: filtering.foremanUserId ?? null,
});

function useDropdownJobPhases({
  enabled,
  filtering,
  sorting,
  offset,
  searchByName,
}) {
  const [getJobPhasesForDropdown, queryContext] = useCustomGetJobPhasesForDropdownLazyQuery();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    getJobPhasesForDropdown({
      variables: {
        ...getSharedVariables({ filtering, sorting, offset }),
        billingStatuses: filtering.billingStatuses,
        searchByName,
        date: getFormattedFilterDate(filtering.startsAt),
      },
      fetchPolicy: "no-cache",
    });
  }, [
    enabled,
    filtering.billingStatuses,
    filtering.companyId,
    filtering.crewID,
    filtering.debouncedSearchTerm,
    filtering.foremanUserId,
    filtering.startsAt,
    filtering.userID,
    getJobPhasesForDropdown,
    offset.first,
    offset.page,
    searchByName,
    sorting.sortDirection,
    sorting.sortKey,
  ]);

  return {
    jobPhasesData: queryContext.data?.getJobPhases.data as any[] | undefined,
    paginationData: queryContext.data?.getJobPhases?.paginatorInfo as any,
    queryLoading: queryContext.loading,
    queryError: queryContext.error,
  };
}

function useTableJobPhases({ enabled, filtering, sorting, offset, searchByName }) {
  const [getJobPhases, standardContext] = useCustomGetJobPhasesLazyQuery();
  const [getJobPhasesScheduleBlocks, scheduleBlocksContext] =
    useCustomGetJobPhasesScheduleBlocksLazyQuery();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const sharedVariables = getSharedVariables({ filtering, sorting, offset });
    const formattedDate = getFormattedFilterDate(filtering.startsAt);

    if (formattedDate) {
      getJobPhasesScheduleBlocks({
        variables: {
          ...sharedVariables,
          date: formattedDate,
        },
        fetchPolicy: "no-cache",
      });

      return;
    }

    getJobPhases({
      variables: {
        ...sharedVariables,
        billingStatuses: filtering.billingStatuses,
        searchByName,
        date: formattedDate,
      },
      fetchPolicy: "no-cache",
    });
  }, [
    enabled,
    filtering.billingStatuses,
    filtering.companyId,
    filtering.crewID,
    filtering.debouncedSearchTerm,
    filtering.foremanUserId,
    filtering.startsAt,
    filtering.userID,
    getJobPhases,
    getJobPhasesScheduleBlocks,
    offset.first,
    offset.page,
    searchByName,
    sorting.sortDirection,
    sorting.sortKey,
  ]);

  if (filtering.startsAt) {
    return {
      jobPhasesData: scheduleBlocksContext.data?.getJobPhases.data as any[] | undefined,
      paginationData: scheduleBlocksContext.data?.getJobPhases?.paginatorInfo as any,
      queryLoading: scheduleBlocksContext.loading,
      queryError: scheduleBlocksContext.error,
    };
  }

  return {
    jobPhasesData: standardContext.data?.getJobPhases.data as any[] | undefined,
    paginationData: standardContext.data?.getJobPhases?.paginatorInfo as any,
    queryLoading: standardContext.loading,
    queryError: standardContext.error,
  };
}

export default function useGetJobPhases({
  filtering,
  sorting,
  offset,
  searchByName = false,
  isForDropdown = false,
}) {
  const dropdownResult = useDropdownJobPhases({
    enabled: isForDropdown,
    filtering,
    sorting,
    offset,
    searchByName,
  });

  const tableResult = useTableJobPhases({
    enabled: !isForDropdown,
    filtering,
    sorting,
    offset,
    searchByName,
  });

  return isForDropdown ? dropdownResult : tableResult;
}
